// LocalStorage service for storing and retrieving address data across pages
// Used to keep address information synchronized when websocket updates arrive

const STORAGE_KEYS = {
    ADDRESSES: 'crypto_notifications_addresses',
    ADDRESS_DATA: 'crypto_notifications_address_data',
    LAST_UPDATED: 'crypto_notifications_last_updated'
};

// Memory optimization: limits for stored data
const MAX_TRANSACTIONS_PER_ADDRESS = 25; // Reduced from 100
const MAX_APPROVALS_PER_ADDRESS = 25;
const MAX_EVENTS_PER_ADDRESS = 25;
const MAX_TOTAL_ADDRESSES = 20;
const MAX_STORAGE_SIZE_MB = 5; // Max localStorage size in MB

// Throttle configuration to prevent excessive localStorage writes
const WRITE_THROTTLE_MS = 2000; // Write at most once per 2 seconds
let pendingWrites = new Map(); // address -> data
let writeTimeout = null;

// Trim large arrays to prevent memory bloat
const trimArray = (arr, maxLength) => {
    if (!Array.isArray(arr)) return arr;
    return arr.slice(0, maxLength);
};

// Clean address data to reduce memory footprint
const cleanAddressData = (data) => {
    if (!data) return data;
    return {
        ...data,
        transactions: trimArray(data.transactions, MAX_TRANSACTIONS_PER_ADDRESS),
        approvals: trimArray(data.approvals, MAX_APPROVALS_PER_ADDRESS),
        events: trimArray(data.events, MAX_EVENTS_PER_ADDRESS),
        // Remove large nested data that's not essential
        chains: data.chains ? Object.fromEntries(
            Object.entries(data.chains).map(([chain, chainData]) => [
                chain,
                {
                    ...chainData,
                    events: trimArray(chainData?.events, MAX_EVENTS_PER_ADDRESS),
                    transactions: trimArray(chainData?.transactions, MAX_TRANSACTIONS_PER_ADDRESS),
                    approvals: trimArray(chainData?.approvals, MAX_APPROVALS_PER_ADDRESS),
                    // Keep only essential balance data
                    balances: trimArray(chainData?.balances, 50)
                }
            ])
        ) : {}
    };
};

// Process all pending writes in a single batch
const flushPendingWrites = () => {
    if (pendingWrites.size === 0) return;
    
    try {
        let existingData = JSON.parse(localStorage.getItem(STORAGE_KEYS.ADDRESS_DATA) || '{}');
        
        // Clean existing data to reduce size
        Object.keys(existingData).forEach(addr => {
            if (existingData[addr]?.data) {
                existingData[addr].data = cleanAddressData(existingData[addr].data);
            }
        });
        
        // Limit total number of addresses stored
        const addressKeys = Object.keys(existingData);
        if (addressKeys.length > MAX_TOTAL_ADDRESSES) {
            // Remove oldest entries
            const sortedKeys = addressKeys.sort((a, b) => {
                const timeA = new Date(existingData[a]?.lastUpdated || 0).getTime();
                const timeB = new Date(existingData[b]?.lastUpdated || 0).getTime();
                return timeB - timeA; // Newest first
            });
            existingData = Object.fromEntries(
                sortedKeys.slice(0, MAX_TOTAL_ADDRESSES).map(key => [key, existingData[key]])
            );
        }
        
        pendingWrites.forEach((data, address) => {
            existingData[address] = {
                data: cleanAddressData(data),
                lastUpdated: new Date().toISOString()
            };
        });
        
        // Check storage size and prune if needed
        const dataStr = JSON.stringify(existingData);
        const sizeInMB = new Blob([dataStr]).size / (1024 * 1024);
        
        if (sizeInMB > MAX_STORAGE_SIZE_MB) {
            console.warn(`[LocalStorage] Data size ${sizeInMB.toFixed(2)}MB exceeds limit, pruning old data`);
            // Remove half of the addresses (oldest first)
            const keys = Object.keys(existingData).sort((a, b) => {
                const timeA = new Date(existingData[a]?.lastUpdated || 0).getTime();
                const timeB = new Date(existingData[b]?.lastUpdated || 0).getTime();
                return timeB - timeA;
            });
            existingData = Object.fromEntries(
                keys.slice(0, Math.ceil(keys.length / 2)).map(key => [key, existingData[key]])
            );
        }
        
        localStorage.setItem(STORAGE_KEYS.ADDRESS_DATA, JSON.stringify(existingData));
        localStorage.setItem(STORAGE_KEYS.LAST_UPDATED, new Date().toISOString());
        pendingWrites.clear();
    } catch (error) {
        console.error('Error flushing pending writes:', error);
        // If localStorage is full, clear it and try again
        if (error.name === 'QuotaExceededError') {
            console.warn('[LocalStorage] Quota exceeded, clearing old data');
            localStorage.removeItem(STORAGE_KEYS.ADDRESS_DATA);
            pendingWrites.clear();
        }
    }
    
    writeTimeout = null;
};

class LocalStorageService {
    // Store addresses data (throttled)
    static setAddressData(address, data) {
        try {
            // Queue the write
            pendingWrites.set(address.toLowerCase(), data);
            
            // Schedule flush if not already scheduled
            if (!writeTimeout) {
                writeTimeout = setTimeout(flushPendingWrites, WRITE_THROTTLE_MS);
            }
        } catch (error) {
            console.error('Error queueing address data:', error);
        }
    }
    
    // Immediately flush pending writes (useful before page unload)
    static flushWrites() {
        if (writeTimeout) {
            clearTimeout(writeTimeout);
        }
        flushPendingWrites();
    }

    // Get specific address data
    static getAddressData(address) {
        try {
            const allData = this.getAllAddressData();
            return allData[address.toLowerCase()];
        } catch (error) {
            console.error('Error getting address data:', error);
            return null;
        }
    }

    // Get all stored address data
    static getAllAddressData() {
        try {
            const storedData = localStorage.getItem(STORAGE_KEYS.ADDRESS_DATA);
            return storedData ? JSON.parse(storedData) : {};
        } catch (error) {
            console.error('Error getting all address data:', error);
            return {};
        }
    }

    // Update balance information for an address
    static updateAddressBalance(address, balanceUpdate) {
        try {
            const existingData = this.getAddressData(address.toLowerCase());
            if (existingData && existingData.data) {
                // Update top-level balance fields
                existingData.data = {
                    ...existingData.data,
                    balance: balanceUpdate.balance,
                    balanceEth: balanceUpdate.balanceEth,
                    lastBalanceUpdate: new Date().toISOString(),
                    deltaEth: balanceUpdate.deltaEth || existingData.data.deltaEth,
                    previousBalanceEth: balanceUpdate.previousBalanceEth || existingData.data.previousBalanceEth
                };

                // Also update the chains structure if it exists
                if (existingData.data.chains?.ethereum?.balances) {
                    existingData.data.chains.ethereum.balances = existingData.data.chains.ethereum.balances.map(b => {
                        // Update native balance entry
                        if (b.isNative || b.contractAddress === null ||
                            (b.tokenId && b.tokenId.includes('0x0000000000000000000000000000000000000000'))) {
                            return {
                                ...b,
                                balanceEth: balanceUpdate.balanceEth,
                                amount: balanceUpdate.balance,
                                balance: balanceUpdate.balance,
                                valueUsd: balanceUpdate.balanceEth * (b.priceUsd || 3500)
                            };
                        }
                        return b;
                    });
                }

                this.setAddressData(address, existingData.data);

                return true;
            }
            return false;
        } catch (error) {
            console.error('Error updating address balance:', error);
            return false;
        }
    }

    // Update transaction data for an address
    static updateAddressTransactions(address, newTransactions) {
        try {
            const existingData = this.getAddressData(address.toLowerCase());
            if (existingData) {
                existingData.data = {
                    ...existingData.data,
                    transactions: newTransactions,
                    lastTransactionUpdate: new Date().toISOString()
                };
                this.setAddressData(address, existingData.data);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error updating address transactions:', error);
            return false;
        }
    }

    // Store addresses list
    static setAddressesList(addresses) {
        try {
            localStorage.setItem(STORAGE_KEYS.ADDRESSES, JSON.stringify(addresses));
        } catch (error) {
            console.error('Error storing addresses list:', error);
        }
    }

    // Get addresses list
    static getAddressesList() {
        try {
            const stored = localStorage.getItem(STORAGE_KEYS.ADDRESSES);
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            console.error('Error getting addresses list:', error);
            return [];
        }
    }

    // Check if data is stale (older than cache timeout)
    static isDataStale(lastUpdated, maxAgeMinutes = 5) {
        if (!lastUpdated) return true;

        const now = new Date();
        const updatedTime = new Date(lastUpdated);
        const diffMinutes = (now - updatedTime) / (1000 * 60);

        return diffMinutes > maxAgeMinutes;
    }

    // Sync address data from API to localStorage
    static syncAddressDataFromAPI(addressData) {
        try {
            if (!addressData.address) return;

            const processedData = {
                address: addressData.address,
                balance: addressData.balanceEth || addressData.balance,
                balanceEth: addressData.balanceEth,
                transactions: addressData.transactions || [],
                approvals: addressData.approvals || [],
                events: addressData.events || [],
                chains: addressData.chains || {},
                lastUpdated: new Date().toISOString()
            };

            this.setAddressData(addressData.address, processedData);
        } catch (error) {
            console.error('Error syncing address data from API:', error);
        }
    }

    // Clear all stored data
    static clearAll() {
        try {
            // Flush any pending writes first
            this.flushWrites();
            Object.values(STORAGE_KEYS).forEach(key => {
                localStorage.removeItem(key);
            });
        } catch (error) {
            console.error('Error clearing localStorage:', error);
        }
    }

    // Update address data from websocket balance update
    static handleWebSocketBalanceUpdate(balanceData) {
        if (!balanceData || !balanceData.address) return;

        const address = balanceData.address.toLowerCase();

        // Check if this address is being watched by looking at stored addresses list
        const watchedAddresses = this.getAddressesList();
        const isWatched = watchedAddresses.some(addr => {
            const addrStr = typeof addr === 'string' ? addr : addr.address;
            return addrStr && addrStr.toLowerCase() === address;
        });

        if (!isWatched) {
            return; // Don't store data for non-watched addresses
        }

        const updateData = {
            balanceEth: balanceData.balanceEth,
            balance: balanceData.balance,
            deltaEth: balanceData.deltaEth,
            previousBalanceEth: balanceData.previousBalanceEth,
            timestamp: balanceData.timestamp || new Date().toISOString()
        };

        this.updateAddressBalance(address, updateData);

        // Trigger UI update event
        window.dispatchEvent(new CustomEvent('addressBalanceUpdated', {
            detail: { address: balanceData.address, balanceData: updateData }
        }));
    }

    // Update address data from websocket transaction update
    static handleWebSocketTransactionUpdate(transactionData) {
        if (!transactionData || !transactionData.txHash) return;

        const transactionAddress = transactionData.from || transactionData.to;
        if (!transactionAddress) return;

        const address = transactionAddress.toLowerCase();

        // Check if this address is being watched
        const watchedAddresses = this.getAddressesList();
        const isWatched = watchedAddresses.some(addr => {
            const addrStr = typeof addr === 'string' ? addr : addr.address;
            return addrStr && addrStr.toLowerCase() === address;
        });

        if (!isWatched) {
            return; // Don't store transaction data for non-watched addresses
        }

        const updateData = {
            newTransaction: {
                txHash: transactionData.txHash,
                from: transactionData.from,
                to: transactionData.to,
                valueEth: transactionData.valueEth,
                timestamp: transactionData.timestamp || new Date().toISOString()
            }
        };

        // Add to existing transactions array
        const existingData = this.getAddressData(address);
        if (existingData && existingData.data) {
            const transactions = existingData.data.transactions || [];
            transactions.unshift(updateData.newTransaction); // Add to beginning

            this.updateAddressTransactions(address, transactions.slice(0, MAX_TRANSACTIONS_PER_ADDRESS));
        }

        // Trigger UI update event
        window.dispatchEvent(new CustomEvent('addressTransactionUpdated', {
            detail: { address: address, transactionData: updateData.newTransaction }
        }));
    }
}

export default LocalStorageService;
